import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import heroImage from '../assets/nails-hero.png';
import Footer from '../components/Footer';
import './NailShop.css';
import now from '../assets/now.png';
import again from '../assets/again.png';
import experience from '../assets/experience.png';
import yourself from '../assets/yourself.png';
import signatureSets from '../data/signatureSets';
import onHandProducts from '../data/onHandProducts';
import { imgSrc } from '../utils/img';
import { formatPrice as formatPriceCents } from '../utils/money';

// --------- Cards for main 4-lane section ----------
const cards = [
  {
    id: 'onhand',
    title: "Nail'd It Now!",
    blurb: 'One-of-one sets. What you see is what ships.',
    cta: 'Shop On-Hand',
    to: '/nails/onhand',
    badge: 'Ready to Ship',
    bg: 'from-pink-200 to-pink-300',
    thumbnail: now,
  },
  {
    id: 'signature',
    title: "Nail'd It Again!",
    blurb: 'Iconic NeNe sets recreated exactly as shown.',
    cta: 'Browse Signature',
    to: '/nails/signature',
    badge: 'Recreatable',
    bg: 'from-purple-200 to-purple-300',
    thumbnail: again,
  },
  {
    id: 'experience',
    title: "NeNe Nail’d It Experience",
    blurb: 'On-site glam & installation. ATL + beyond.',
    cta: 'Book the Experience',
    to: '/nails/experience',
    badge: 'On-Site',
    bg: 'from-emerald-200 to-emerald-300',
    thumbnail: experience,
  },
  {
    id: 'custom',
    title: 'Custom',
    blurb: 'Your color, shape, length, and add-ons.',
    cta: 'Start Custom',
    to: '/nails/custom',
    badge: 'Build Your Own',
    bg: 'from-blue-200 to-blue-300',
    thumbnail: yourself,
  },
];

// --------- Helper: build featured preview ----------
const featuredPreview = (() => {
  const FEATURED_ORDER = [
    'muse-halloween-med-square', 
    'muse-vamp-princess-med-square',
    'sig-gender-reveal',  
    'fnf-blue-chrome-med-almond',
    'sig-bloody-shoes',            
     // add/remove as needed
  ];

  const sig = signatureSets
    .filter((s) => s.featured || s.tags?.some((t) => /holiday|celeb/i.test(t)))
    .map((s) => ({
      type: 'signature',
      id: s.id,
      slug: s.slug,
      title: s.title,
      tierOrStyle: 'Signature',
      priceLabel: formatPriceCents(s.priceCents),
      thumbnail: s.thumbnail,
    }));

  const parsePriceToLabel = (p, cents) => {
    if (p) return p;
    if (typeof cents === 'number') return formatPriceCents(cents);
    return '';
  };

  const onh = onHandProducts
    .filter((o) => o.featured || o.tags?.some((t) => /holiday|celeb/i.test(t)))
    .map((o) => ({
      type: 'onhand',
      id: o.id,
      slug: o.slug,
      title: o.color || o.style || o.id,
      tierOrStyle: o.style || 'On Hand',
      priceLabel: parsePriceToLabel(o.price, o.priceCents),
      thumbnail: o.thumbnail,
    }));

  const merged = [...sig, ...onh];

  // Return merged items in your chosen order
  return FEATURED_ORDER.map((id) => merged.find((m) => m.id === id)).filter(Boolean);
})();


export default function NailShop() {
  return (
    <>
      <Helmet>
        <title>NeNeNail’dIt | Custom Press-On Nails by NeNe</title>
        <meta
          name="description"
          content="Shop custom press-on nails designed by NeNe. Choose your shape, length, and vibe. Bold, soft, or fully custom — we’ve got your hands covered."
        />
        <meta
          name="keywords"
          content="custom press-on nails, almond nails, square nails, gel press-ons, NeNe Nails, handmade nails, nail art"
        />
        <meta name="author" content="NeNe The Architect" />
        <link rel="canonical" href="https://nenethearchitect.com/nails" />
        <meta property="og:title" content="NeNeNail’dIt | Press-On Nail Sets" />
        <meta
          property="og:description"
          content="Fully customizable, press-on nails designed to fit your style. Shop sets or build your own."
        />
        <meta property="og:url" content="https://nenethearchitect.com/nails" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Store",
              "name": "NeNeNail’dIt",
              "url": "https://nenethearchitect.com/nails",
              "logo": "https://nenethearchitect.com/logo.png",
              "description": "Custom press-on nail sets by NeNe. Choose your color, shape, and length and design.",
              "sameAs": [
                "https://www.instagram.com/neneeenaildit",
                "https://www.tiktok.com/@nenethearchitect"
              ]
            }
          `}
        </script>
      </Helmet>

      <div className="nailshop-wrapper pt-20 md:pt-0">
        {/* HERO */}
        <section className="nailshop-hero relative -mt-16 pb-10 md:pb-8">
          <img src={heroImage} alt="Nail Shop Hero" className="nailshop-hero-image" />
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-4">
            Premium Press-Ons for Bold Babes
          </h2>
          <p className="text-lg md:text-xl text-center max-w-2xl mx-auto text-gray-700 leading-relaxed">
            Pick your vibe — I’ll nail it. Whether you want it fast, want it iconic, want me
            on-site, or want it custom from scratch, NeNe’s got you. Four lanes, one destination:
            hands that turn heads.
          </p>
        </section>

        {/* FEATURED — now centered between hero and hub */}
        <section className="py-14 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-semibold text-center mb-10">Featured Sets</h2>

            {/* Mobile scroll */}
            <div className="md:hidden -mx-4 px-4 overflow-x-auto">
              <div className="flex gap-4">
                {featuredPreview.map((it) => (
                  <Link
                    key={it.id}
                    to={
                      it.type === 'signature'
                        ? `/nails/signature/${it.slug}`
                        : `/nails/onhand/${it.slug}`
                    }
                    className="min-w-[72%] bg-white rounded-2xl shadow hover:shadow-md transition overflow-hidden block"
                  >
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={imgSrc(it.thumbnail)}
                        alt={it.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 text-center">
                      <h3 className="text-lg font-semibold">{it.title}</h3>
                      <p className="text-sm text-gray-600">
                        {it.tierOrStyle} • {it.priceLabel}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Desktop grid */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 justify-center">
              {featuredPreview.map((it) => (
                <Link
                  key={it.id}
                  to={
                    it.type === 'signature'
                      ? `/nails/signature/${it.slug}`
                      : `/nails/onhand/${it.slug}`
                  }
                  className="bg-white rounded-2xl shadow hover:shadow-md transition overflow-hidden block"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={imgSrc(it.thumbnail)}
                      alt={it.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="text-lg font-semibold mb-1">{it.title}</h3>
                    <p className="text-sm text-gray-600">
                      {it.tierOrStyle} • {it.priceLabel}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/nails/featured"
                className="btn-nene inline-flex items-center justify-center px-5 py-2 font-semibold"
              >
                View All Featured →
              </Link>
            </div>
          </div>
        </section>

        {/* FOUR-LANE HUB */}
        <main className="px-4 py-14 max-w-7xl mx-auto">
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cards.map((card) => (
              <Link
                key={card.id}
                to={card.to}
                className="group block rounded-2xl shadow hover:shadow-lg transition overflow-hidden focus:outline-none focus:ring-2 focus:ring-pink-400"
              >
                {/* Gradient header with PNG overlay */}
                <div className="relative h-40 sm:h-44">
                  <div className={`${card.bg}`} />
                  <img
                    src={card.thumbnail}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 m-auto max-h-[100%] w-auto object-contain drop-shadow-lg transition-transform duration-200 group-hover:scale-[1.03] pointer-events-none select-none"
                  />
                </div>

                {/* Body */}
                <div className="p-5 bg-white h-full flex flex-col">
                  <div className="text-xs uppercase tracking-wide bg-black text-white inline-block px-2 py-1 rounded">
                    {card.badge}
                  </div>
                  <h2 className="text-lg font-semibold mt-3">{card.title}</h2>
                  <p className="text-sm text-gray-600 mt-1 flex-1">{card.blurb}</p>
                  <span className="btn-nene mt-4 inline-flex items-center justify-center whitespace-nowrap">
                    {card.cta} <span className="ml-1">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </section>
        </main>
      </div>

      <Footer />
    </>
  );
}
